/**
 * sessionRefresh.ts — обновление пропуска кабинета по ответу 401 (WEB-2613).
 *
 * ЧТО ЛОМАЛОСЬ. Клиент (`api/client.ts`) обновлял пропуск голым
 * `axios.post('/auth/refresh')` мимо общего предела ожидания (WEB-2590).
 * Повис этот запрос — висели исходный запрос и все, кто ждал нового пропуска
 * за ним (координатор `tokenRefresh.ts` держит одно обновление на всех).
 *
 * ЧТО ТЕПЕРЬ (решение владельца 29.09, по рекомендации):
 *   · у обновления общий предел ожидания кабинета (`CLIENT_TIMEOUT_DEFAULTS`);
 *   · истёк предел, оборвалась сеть, сервер ответил 5xx, 408 или 429 —
 *     неудача временная: сессия НЕ завершается, каждый ждавший запрос
 *     получает отказ фразой из словаря без статуса (автосохранение считает
 *     такой отказ временным и повторяет по своим правилам), следующая
 *     попытка обновления идёт сразу;
 *   · выход из кабинета — только когда сервер ответил, что пропуск обновить
 *     нельзя (прочие 4xx; сервер отвечает 401 «Invalid refresh token»).
 *
 * Исходный запрос получил 401 — сервер его не выполнил, поэтому отказ
 * говорит «повторите», а не «результат неизвестен» даже у денежных действий.
 *
 * WEB-3970 кабинет: 401 с кодом из закрытого перечня `isRefreshExemptErrorCode`
 * (неверный пароль или подтверждение Google перед передачей и удалением
 * организации) пропуск не обновляет и запрос не повторяет — отказ уходит дальше
 * обычным путём.
 *
 * Модуль без `import.meta` — поэтому живёт отдельно от `client.ts` (тот в jest
 * не загружается) и проверяется на настоящем axios против настоящего сервера
 * (`api/sessionRefresh.test.ts`).
 */
import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { errorsStrings } from '@/i18n/dictionaries/errors';
import { pickStrings } from '@/i18n/define';
import { CLIENT_TIMEOUT_DEFAULTS, isRequestTimeoutError } from './requestTimeout';
import { isRefreshExemptErrorCode } from './refreshExemptPaths';
import { createTokenRefreshCoordinator } from './tokenRefresh';
import type { RefreshAccessTokenOptions, RefreshResult } from './tokenRefresh';

/** Настройки запроса обновления: общий предел ожидания кабинета. */
export const SESSION_REFRESH_REQUEST_CONFIG: AxiosRequestConfig = { ...CLIENT_TIMEOUT_DEFAULTS };

/** Ответ `handleUnauthorized`: отказ не его — перехватчик разбирает его дальше как обычно. */
export const SESSION_REFRESH_PASS: unique symbol = Symbol('session-refresh-pass');

export interface SessionRefreshDeps {
  readonly baseURL: string;
  readonly getStoredRefreshToken: () => string | null;
  readonly onRefreshed: (result: RefreshResult) => void;
  readonly onSessionExpired: () => void;
  /** Повтор исходного запроса с новым пропуском (в `client.ts` — `api(config)`). */
  readonly resend: (config: InternalAxiosRequestConfig) => Promise<unknown>;
  /** Пути, где 401 значит «неверные данные», а не «сессия истекла». */
  readonly isExemptUrl: (url: string | undefined) => boolean;
  /** Транспорт — только для тестов; по умолчанию axios без перехватчиков. */
  readonly http?: { post: (url: string, body: unknown, config?: AxiosRequestConfig) => Promise<Pick<AxiosResponse, 'data'>> };
  /** Свой предел — только для тестов (не ждать 30 секунд). */
  readonly refreshRequestConfig?: AxiosRequestConfig;
}

export interface SessionRefresh {
  readonly refreshAccessToken: (options?: RefreshAccessTokenOptions) => Promise<string>;
  /**
   * Ветка 401 перехватчика: обновить пропуск и повторить запрос. Возвращает
   * ответ повтора, `SESSION_REFRESH_PASS` (отказ не его, либо сервер отказал
   * в обновлении — сессия уже завершена, разбирать исходный 401 как прежде)
   * или отклоняется отказом «вход не обновился», если неудача временная.
   */
  readonly handleUnauthorized: (error: unknown) => Promise<unknown>;
}

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

/** Временная ли неудача обновления: ответа нет, либо 5xx / 408 / 429. */
export function isTransientRefreshFailure(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const { isAxiosError, response } = err as { isAxiosError?: unknown; response?: { status?: unknown } };
  if (isAxiosError !== true) return false;
  const status = response?.status;
  if (typeof status !== 'number') return true;
  return status >= 500 || status === 408 || status === 429;
}

/** Отказ ждавшему запросу: вход не обновился, человек остаётся в кабинете. */
export function sessionRefreshUnavailableError(cause: unknown): Error {
  const error = Object.assign(new Error(pickStrings(errorsStrings).sessionRefreshUnavailable), {
    refreshUnavailable: true as const,
  });
  return isRequestTimeoutError(cause) ? Object.assign(error, { timedOut: true as const }) : error;
}

export function createSessionRefresh(deps: SessionRefreshDeps): SessionRefresh {
  const http = deps.http ?? axios;
  const requestConfig = deps.refreshRequestConfig ?? SESSION_REFRESH_REQUEST_CONFIG;

  // Голый транспорт, не общий клиент: через него запрос вошёл бы в тот же
  // перехватчик ответа и понёс бы протухший заголовок пропуска; ручке
  // обновления не нужно ни то, ни другое — пропуск обновления едет в теле.
  const coordinator = createTokenRefreshCoordinator({
    getStoredRefreshToken: deps.getStoredRefreshToken,
    postRefresh: async (refreshToken) => {
      const response = await http.post(`${deps.baseURL}/auth/refresh`, { refreshToken }, requestConfig);
      return {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
      };
    },
    onRefreshed: deps.onRefreshed,
    onSessionExpired: deps.onSessionExpired,
    isTransientFailure: isTransientRefreshFailure,
  });

  async function handleUnauthorized(error: unknown): Promise<unknown> {
    const e = error as { config?: RetryableConfig; response?: { status?: number; data?: { code?: unknown } } } | null;
    const config = e?.config;
    if (e?.response?.status !== 401 || !config || config._retry || deps.isExemptUrl(config.url)) {
      return SESSION_REFRESH_PASS;
    }
    // WEB-3970 кабинет: 401 «подтверждение не подошло» (закрытый перечень кодов) —
    // не истёкшая сессия; повтор засчитал бы одну неверную попытку дважды.
    if (isRefreshExemptErrorCode(e.response.data?.code)) return SESSION_REFRESH_PASS;
    // Одна попытка на запрос: 401 и после обновления (отозванная сессия,
    // чужие права) уходит дальше обычным отказом, без петли.
    config._retry = true;
    let accessToken: string;
    try {
      accessToken = await coordinator.refreshAccessToken();
    } catch (refreshError) {
      if (isTransientRefreshFailure(refreshError)) throw sessionRefreshUnavailableError(refreshError);
      // Сервер отказал в обновлении (сессия уже завершена координатором) или
      // пропуска обновления нет — исходный 401 разбирается как прежде.
      return SESSION_REFRESH_PASS;
    }
    config.headers.Authorization = `Bearer ${accessToken}`;
    return deps.resend(config);
  }

  return { refreshAccessToken: coordinator.refreshAccessToken, handleUnauthorized };
}
